// packages/app-shell/src/process/useProcessOrchestrator.ts
//
// Shared process-walk derivation. Given the wire model + an active process id +
// the per-process walker state, derive everything <ProcessMode> needs: the
// active process, its subprocess lookup, the swimlane bridge context, and the
// walk result. Both the full app shell AND embedded surfaces (Studio's editor
// preview) drive process rendering through this one source of truth.
//
// v0.10 single render path (VIEWS.md §intro): opening a process tab renders a
// synthesized `view { flow <That> }` — the exact analog of the board's
// synthesized `view Default {}`. The tab's SUBJECT resolves through that view
// (flowData → viewer-core `resolveFlowProcess`, internal handle incl.
// anonymous runs), and the walker/swimlane contexts come from the engine's
// `walkerContextFor`/`swimlaneContextFor` — the SAME builders the explicit
// flow-view surfaces and the headless renders consume. The wire-model scope
// walks that used to live here (and drifted: the wire hoist index never
// recursed into nested modules) are gone; playback is the view-channel
// interaction layered on top (VIEWS.md §6).

import { useMemo } from "react";
import type {
  ArchitectureModel,
  StableId,
  Subprocess,
} from "@archlang/scene/arch-model";
import type {
  WalkerState,
  SwimlaneContext,
  ResolvedModel,
  ProcessModel,
} from "@archlang/engine";
import { swimlaneContextFor, walkerContextFor } from "@archlang/engine";
import { flowProcessOf, synthesizedFlowView } from "../views/flowData.js";
import { processKey } from "../processKey.js";
import { useProcessWalker } from "./useProcessWalker.js";

export interface ProcessOrchestration {
  readonly activeProcess: ArchitectureModel["processes"][number] | null;
  /** The resolved IR of {@link activeProcess} (`flowProcessOf`) — the engine
   *  `ProcessModel` consumers that walk/enumerate need (tests, coverage, pin
   *  replay), distinct from the AST `activeProcess`. Null off a resolved model. */
  readonly activeProcessModel: ProcessModel | null;
  readonly subprocessByName: Map<string, Subprocess>;
  readonly swimlaneContext: SwimlaneContext;
  readonly walkResult: ReturnType<typeof useProcessWalker>["walkResult"];
  readonly walkResultAllBranches: ReturnType<typeof useProcessWalker>["walkResultAllBranches"];
  readonly edgeDecoMap: ReturnType<typeof useProcessWalker>["edgeDecoMap"];
}

const EMPTY_WALKER_CONTEXT = {
  subprocessByName: new Map(),
  processByName: new Map(),
} as const;

/** The no-model fallback (mirrors the old wire-context behavior with an empty
 *  module tree: callee = written module path, caller unresolved, single
 *  unknown segment reads as an actor). */
const EMPTY_SWIMLANE_CONTEXT = {
  resolveCalleeModule: (to: readonly string[]) => to.slice(0, -1),
  resolveCallerModule: () => null,
  isActor: (from: readonly string[]) => from.length === 1,
} as unknown as SwimlaneContext;

export function useProcessOrchestrator(
  model: ArchitectureModel | null,
  activeProcessId: StableId | null,
  walkerState: WalkerState,
  activeStepIndex: number | null,
  // The resolved engine model for the current compile — the synthesized flow
  // view resolves the process IR off it, and the walker/swimlane contexts
  // build over it. Null (embedded callers without a compile) = empty walk.
  resolvedModel: ResolvedModel | null = null,
): ProcessOrchestration {
  // The active process (wire), keyed by the same stable key the nav selects on.
  const activeProcess = useMemo(() => {
    if (!model || !activeProcessId) return null;
    return model.processes.find((p) => processKey(p) === activeProcessId) ?? null;
  }, [model, activeProcessId]);

  // The active process's IR body — resolved through the synthesized
  // `view { flow <handle> }`, the same path an explicit flow view rides. The
  // handle is the tab's `processKey`: #id, qualified name, or the anonymous
  // run's content-keyed identity.
  const activeProcessModel = useMemo(() => {
    if (!resolvedModel || !activeProcessId) return null;
    return flowProcessOf(synthesizedFlowView(activeProcessId), resolvedModel);
  }, [resolvedModel, activeProcessId]);

  // Subprocess lookup *for the active process* over the WIRE model (the
  // process panels' description/readme source). Lowest priority first (later
  // writes win): free-standing top-level subprocesses, then module-scoped ones
  // whose owner is an ancestor of the active process's owner (closer ancestors
  // win — iterated in length order), then subprocesses declared lexically
  // inside the active process body.
  const subprocessByName = useMemo(() => {
    const m = new Map<string, Subprocess>();
    if (!model) return m;
    const activeOwner = activeProcess?.ownerPath ?? [];
    const activeSpace = activeProcess?.space ?? "";
    const isPrefix = (a: readonly string[], b: readonly string[]) =>
      a.length <= b.length && a.every((seg, i) => seg === b[i]);
    const scoped: Subprocess[] = [];
    // Free-standing lookup is SPACE-AWARE (v0.11, mirrors the engine's
    // splice-scope chain): the active process's own space wins; another
    // space's same-named subprocess stays only as the off-chain fallback —
    // without the split, a cross-space twin last-wins BOTH spaces' panels.
    const freeSame: Subprocess[] = [];
    const freeOther: Subprocess[] = [];
    for (const sp of model.subprocesses ?? []) {
      if (!sp.ownerPath || sp.ownerPath.length === 0) {
        ((sp.space ?? "") === activeSpace ? freeSame : freeOther).push(sp);
      } else if (
        activeProcess &&
        isPrefix(sp.ownerPath, activeOwner) &&
        (sp.space ?? "") === activeSpace
      ) {
        // Module-scoped: the owner chain lives in the process's landed space —
        // a same-path owner in ANOTHER space is a different module (v0.11).
        scoped.push(sp);
      }
    }
    for (const sp of freeOther) m.set(sp.name, sp);
    for (const sp of freeSame) m.set(sp.name, sp);
    scoped.sort((a, b) => (a.ownerPath?.length ?? 0) - (b.ownerPath?.length ?? 0));
    for (const sp of scoped) m.set(sp.name, sp);
    if (activeProcess) {
      for (const sp of activeProcess.subprocesses ?? []) m.set(sp.name, sp);
    }
    return m;
  }, [model, activeProcess]);

  // Walker scope (subprocess inlining + `do <Process>` fallback targets) —
  // the engine's builder, semantics ported FROM this hook (process-contexts.ts)
  // and shared with every flow-view/headless surface since.
  const walkerContext = useMemo(() => {
    if (!resolvedModel || !activeProcessModel) return EMPTY_WALKER_CONTEXT;
    return walkerContextFor(resolvedModel, activeProcessModel);
  }, [resolvedModel, activeProcessModel]);

  // Swimlane semantic resolution (hoisting, bind aliases, actors) — the
  // engine's builder over the resolved module tree, shared with the flow-view
  // surfaces. Unlike the old wire walk, its hoist index covers NESTED modules,
  // so a short ref to a nested module lands on ONE lane.
  const swimlaneContext = useMemo(() => {
    if (!resolvedModel) return EMPTY_SWIMLANE_CONTEXT;
    return swimlaneContextFor(resolvedModel);
  }, [resolvedModel]);

  const { walkResult, walkResultAllBranches, edgeDecoMap } = useProcessWalker({
    process: activeProcessModel,
    state: walkerState,
    subprocessByName: walkerContext.subprocessByName,
    processByName: walkerContext.processByName,
    activeStepIndex,
  });

  return {
    activeProcess,
    activeProcessModel,
    subprocessByName,
    swimlaneContext,
    walkResult,
    walkResultAllBranches,
    edgeDecoMap,
  };
}
